<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ event.text }} ({{ displayedStartDate }}<template v-if="!event.upcoming"> {{ displayedStartTime }} - {{ displayedEndDate }} {{ displayedEndTime }}</template>)</h4>
    </div>
    <div class="modal-body">
      <div class="row">
        <div class="col-md-12">
          <h4 id="showDescription"></h4>
          <div class="form-group" id="showProductSelectorContainer" v-if="!event.product_id || productId">
            <label for="showProductSelector">{{ _('Event') }}</label><br />
            <model-list-select v-if="products" :list="products" option-value="id" option-text="name" v-model="selectedProduct" :placeholder="_('Type to search products')">
            </model-list-select>
          </div>
          <div class="form-group">
            <label class="checkbox-inline"><input type="checkbox" id="showUpcoming" v-model="event.upcoming" :true-value="1" :false-value="0">{{ _('Upcoming show') }}</label>
          </div>
          <label v-if="!parseInt(event.upcoming)">{{ _('Start and end time') }}</label>
          <div class="flex-row form-group" v-if="!parseInt(event.upcoming)">
            <TimePicker :shortLocale="shortLocale" v-model="startTime" :placeholder="_('Select start time')"/>
            <p class="form-control-static">&nbsp;-&nbsp;</p>
            <TimePicker :shortLocale="shortLocale" v-model="endTime" :placeholder="_('Select end time')"/>
            <DatePicker v-model="endDate" :shortLocale="shortLocale" :staticInput="true" :placeholder="_('Select end date')"></DatePicker>              
          </div>
          <div>
            <div class="form-group">
              <label for="showResourceSelector">{{ _('Theater') }}</label><br />
              <select class="form-control" id="showResourceSelector" v-model="event.resource_id" :placeholder="_('Loading venues, please wait...')">
                <option v-if="!resources && event.product_id">{{ _('Loading venues, please wait...') }}</option>
                <option v-if="!resources && !event.product_id">{{ _('Please select a product.') }}</option>
                <option v-else v-for="resource in resources" :value="resource.id">{{ resource.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label for="showPricingSelector">{{ _('Pricing') }}</label><br />
              <select class="form-control" id="showPricingSelector" v-model="pricingId" :placeholder="_('Loading pricing models, please wait...')">
                <option v-if="!pricingModels">{{ _('Loading pricing models, please wait...') }}</option>
                <option v-else v-for="pricing in pricingModels" :value="pricing.id">{{ pricing.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label for="showShowSeatMap">{{ _('Show seat map') }}</label><br />
              <select class="form-control" id="showShowSeatMap" v-model="event.showseatmap">
                <option value="1" :disabled="!seatMapId">{{ _('Yes') }}</option>
                <option value="0">{{ _('No') }}</option>
              </select>
            </div>
            <div class="form-group flex-row">
              <label class="checkbox-inline"><input id="showHideInternet" type="checkbox" v-model="event.hideinternet" :true-value="1" :false-value="0">{{ _('Hide from Internet') }}</label>
              <label class="checkbox-inline"><input id="showHidePos" type="checkbox" v-model="event.hidepos" :true-value="1" :false-value="0">{{ _('Hide from POS') }}</label>
            </div>
            <div class="form-group show-labels">
              <label>{{ _('Show labels') }}</label>
              <p class="help-block">{{ _('Shown to customers next to the show time, for example Premiere or Preview.') }}</p>
              <div class="show-label-options">
                <span v-if="!showLabels" class="text-muted">{{ _('Loading...') }}</span>
                <button v-else v-for="label in showLabels" :key="label.id" type="button" class="show-label-option" :class="{ selected: isLabelSelected(label) }" :style="isLabelSelected(label) ? showLabelStyle(label) : null" :aria-pressed="isLabelSelected(label) ? 'true' : 'false'" v-on:click="toggleLabel(label)">
                  <i class="fa fa-fw" :class="isLabelSelected(label) ? 'fa-check' : 'fa-plus'"></i>{{ label.name }}
                </button>
              </div>
              <div class="input-group show-label-new">
                <input type="text" class="form-control input-sm" v-model="newLabelName" :placeholder="_('New label')" v-on:keydown.enter.prevent="createLabel" />
                <span class="input-group-btn">
                  <button type="button" class="btn btn-default btn-sm" :disabled="!newLabelName.trim() || creatingLabel" v-on:click="createLabel"><i class="fa fa-fw fa-plus"></i> {{ _('Create new...') }}</button>
                </span>
              </div>
            </div>
            <div class="flex-row form-group" id="multiplyContainer" v-if="multiplyDates.length">
              <label v-for="md in multiplyDates" class="checkbox-inline">
                <input class="multiplydate" type="checkbox" :value="DateTime.fromJSDate(md).toUnixInteger()" v-model="selectedDates">{{ multiplyDate(md) }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <div class="flex-row space-between">
        <div class="button-col">
          <button class="btn btn-danger" v-on:click="deleteEvent"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        </div>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="saveEvent"><i class="fa fa-fw fa-save"></i> {{ _('Save') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import errors from '~/lib/errors.js'
import { mapGetters } from 'vuex'
import { ModelListSelect } from 'vue-search-select'
import { showLabelStyle } from '~/lib/showLabels'

export default {
  data () {
    return {
      DateTime: DateTime,
      event: { ...this.sourceEvent}, // Make a copy of the event so we can make changes
      pricing_id: this.sourceEvent.pricing_id,
      selectedProduct: {},
      products: null,
      resources: null,
      pricingModels: null,
      showLabels: null,
      labelIds: Array.isArray(this.sourceEvent.labels) ? this.sourceEvent.labels.map(l => parseInt(l.id)) : [],
      labelsChanged: false,
      newLabelName: '',
      creatingLabel: false,
      selectedDates: [
        DateTime.fromJSDate(this.sourceEvent.start_date).toUnixInteger()
      ]
    }
  },
  props: ['sourceEvent', 'baseDomain', 'shopId', 'shortLocale', 'apiKey', 'defaultPricing', 'product'],
  mixins: [errors],
  computed: {
    ...mapGetters(['dateFormat', 'timeFormat', 'timezone']),
    productId () {
      return this.selectedProduct && this.selectedProduct.id ? this.selectedProduct.id : null
    },
    seatMapId () {
      if (!this.resources || !this.resources.length || !this.event || !this.event.resource_id) {
        return null
      }
      for (let i = 0; i < this.resources.length; i++) {
        if (this.resources[i].id === this.event.resource_id) {
          return this.resources[i].seatmap_id
        }
      }
      return null
    },
    startTime: {
      get () {
        return {
          hours: DateTime.fromJSDate(this.event.start_date).hour,
          minutes: DateTime.fromJSDate(this.event.start_date).minute,
          seconds: 0
        }
      },
      set (newValue) {
        if (newValue) {
          this.event.start_date = DateTime.fromJSDate(this.event.start_date).set({ hour: newValue.hours, minute: newValue.minutes }).toJSDate()
        } else {
          this.event.start_date = null
        }
      }
    },
    endTime: {
      get () {
        return {
          hours: DateTime.fromJSDate(this.event.end_date).hour,
          minutes: DateTime.fromJSDate(this.event.end_date).minute,
          seconds: 0
        }
      },
      set (newValue) {
        if (newValue) {
          this.event.end_date = DateTime.fromJSDate(this.event.end_date).set({ hour: newValue.hours, minute: newValue.minutes }).toJSDate()
        } else {
          this.event.end_date = null
        }
      }
    },
    pricingId: {
      get () {
        return this.pricing_id
      },
      set (newValue) {
        this.pricing_id = newValue
      }
    },
    startDate () {
      if (!this.event.start_date) {
        return null
      }
      return DateTime.fromJSDate(this.event.start_date).toJSDate()
    },
    endDate: {
      get () {
        if (DateTime.fromJSDate(this.event.start_date).day === DateTime.fromJSDate(this.event.end_date).day) {
          return null
        }
        return DateTime.fromJSDate(this.event.end_date).toJSDate()
      }, 
      set (newValue) {
        if (!newValue) {
          this.removeEndDate()
        }
        if (!(newValue instanceof Date)) {
          return
        }
        let n = DateTime.fromJSDate(newValue)
        this.event.end_date = DateTime.fromJSDate(this.event.end_date).set({ day: n.day, month: n.month }).toJSDate()
      },
    },
    displayedStartDate () {
      return DateTime.fromJSDate(this.event.start_date).toFormat(this.dateFormat)
    },
    displayedStartTime () {
      return DateTime.fromJSDate(this.event.start_date).toFormat(this.timeFormat)
    },
    displayedEndTime () {
      return DateTime.fromJSDate(this.event.end_date).toFormat(this.timeFormat)
    },
    displayedEndDate: {
      get () {
        if (DateTime.fromJSDate(this.event.start_date).day === DateTime.fromJSDate(this.event.end_date).day) {
          return ''
        }
        return DateTime.fromJSDate(this.event.end_date).toFormat(this.dateFormat)
      },
      set (newValue) {
        const validFormats = ['d.M.yyyy', 'dd.MM.yyyy', 'dd.M.yyyy', 'd.MM.yyyy']
        if (newValue && validFormats.some(format => DateTime.fromFormat(newValue, format).isValid)) {
          this.endDate = DateTime.fromFormat(newValue, 'd.M.yyyy').toJSDate()
        }
      }
    },
    multiplyDates () {
      if (parseInt(this.event.upcoming)) {
        return []
      }
      let date = DateTime.fromJSDate(this.event.start_date)
      let currDay = date.set({ weekday: date.weekday >= 5 ? 5 : -2 }); // last friday
      let dates = []
      if (this.event.product_id && !this.productId) {
        return dates
      }
      for (var i = 0; i < 7; i++) {
        dates.push(currDay.toJSDate())
        currDay = currDay.plus({ days: 1 })
      }
      return dates
    },
    finalDates () {
      return this.selectedDates.map(d => { return { start_ts: DateTime.fromSeconds(Number(d), { zone: Intl.DateTimeFormat().resolvedOptions().timeZone }).set({ hour: this.startTime.hours, minute: this.startTime.minutes }).toUnixInteger(), end_ts: DateTime.fromSeconds(Number(d), { zone: Intl.DateTimeFormat().resolvedOptions().timeZone }).set({ hour: this.endTime.hours, minute: this.endTime.minutes }).toUnixInteger() } })
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    convertDateToTimezone(date) {
      // Convert Date object from local timezone to this.timezone, preserving wall-clock time
      const dt = DateTime.fromJSDate(date)
      const reinterpreted = DateTime.fromObject({
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
        second: dt.second,
        millisecond: dt.millisecond
      }, { zone: this.timezone })
      return reinterpreted.toJSDate()
    },
    convertTimestampToTimezone(timestamp) {
      // Convert unix timestamp from local timezone to this.timezone, preserving wall-clock time
      const dt = DateTime.fromSeconds(timestamp, { zone: Intl.DateTimeFormat().resolvedOptions().timeZone })
      const reinterpreted = DateTime.fromObject({
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
        second: dt.second,
        millisecond: dt.millisecond
      }, { zone: this.timezone })
      return reinterpreted.toUnixInteger()
    },
    saveEvent () {
      this.event.pricing_id = this.pricing_id
      const event = Object.assign({}, this.event)
      if (this.multiplyDates && this.multiplyDates.length) {
        event.dates = this.finalDates.map(d => {
          // Convert unix timestamps: reinterpret from local timezone to this.timezone
          return {
            start_ts: this.convertTimestampToTimezone(d.start_ts),
            end_ts: this.convertTimestampToTimezone(d.end_ts)
          }
        })
      }
      if (parseInt(event.upcoming)) {
        event.start_date = DateTime.fromJSDate(event.start_date).startOf('day').toJSDate()
        event.end_date = DateTime.fromJSDate(event.start_date).plus({ days: 1 }).toJSDate()
      }
      // Convert start_date and end_date to this.timezone while keeping wall-clock time
      event.start_date = this.convertDateToTimezone(event.start_date)
      event.end_date = this.convertDateToTimezone(event.end_date)
      if(event.end_date <= event.start_date) {
        this.showError(this._('The end time must be greater than the start time.'))
        return
      }
      // The show's labels are replaced as a whole, so only send them when we know them
      if (Array.isArray(this.sourceEvent.labels) || this.labelsChanged) {
        event.labelIds = this.labelIds.slice()
      }
      this.$emit('saveEvent', event)
    },
    deleteEvent () {
      this.$emit('deleteEvent', this.event)
    },
    removeEndDate () {
      this.endDate = this.event.start_date
    },
    showLabelStyle (label) {
      return showLabelStyle(label)
    },
    isLabelSelected (label) {
      // Older backends return showlabels.json ids as strings and show label ids as numbers
      return this.labelIds.includes(parseInt(label.id))
    },
    toggleLabel (label) {
      this.labelsChanged = true
      const labelId = parseInt(label.id)
      if (this.isLabelSelected(label)) {
        this.labelIds = this.labelIds.filter(id => id !== labelId)
      } else {
        this.labelIds = [...this.labelIds, labelId]
      }
    },
    async loadShowLabels () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/showlabels.json?apikey=' + this.apiKey)
      } catch (e) {
        this.showLabels = []
        return
      }
      this.showLabels = response.data
    },
    async createLabel () {
      const name = this.newLabelName.trim()
      if (!name || this.creatingLabel) {
        return
      }
      this.creatingLabel = true
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/showlabels.json?apikey=' + this.apiKey, { name })
      } catch (e) {
        this.creatingLabel = false
        return this.showError(e)
      }
      this.creatingLabel = false
      this.newLabelName = ''
      await this.loadShowLabels()
      this.toggleLabel({ id: parseInt(response.data) })
    },
    multiplyDate (date) {
      let currDay = DateTime.fromJSDate(date)
      let format = ''
      switch (this.dateFormat) {
        case 'd.M.yyyy':
        case 'd.L.yyyy':
          format = 'd.M.'
          break
        case 'M.d.yyyy':
        case 'L.d.yyyy':
        case 'yyyy.MM.dd':
        case 'yyyy.L.dd':
          format = 'M.d.'
          break
        case 'dd-MM-yyyy':
        case 'dd-L-yyyy':
          format = 'd-M'
          break
        case 'MM-dd-yyyy':
        case 'L-dd-yyyy':
        case 'yyyy-MM-dd':
        case 'yyyy-L-dd':
          format = 'M-d'
          break
        case 'dd/MM/yyyy':
        case 'dd/L/yyyy':
          format = 'd/M'
          break
        case 'MM/dd/yyyy':
        case 'L/dd/yyyy':
        case 'yyyy/MM/dd':
        case 'yyyy/L/dd':
          format = 'M/d'
          break
      }
      return this._(currDay.toFormat('ccc')) + ' ' + currDay.toFormat(format)
    },
    async loadShowProducts () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/products.json?sSearch_3=1-4-all&iSortCol_0=2&iSortingCols=1&sSortDir_0=asc&simple=true')
      } catch (e) {
        return
      }
      this.products = response.data
    },
    async loadShowResources () {
      let data
      if (!this.product) {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/products/' + this.event.product_id + '.json')
        } catch (e) {
          return
        }
        data = response.data
      } else {
        data = this.product
      }
      let resources = [];
      if(data.sharedResourcepool) {
        for (var i = 0; i < data.sharedResourcepool.length; i++) {
          if (data.sharedResourcepool[i].sharedResource) {
            for (var j = 0; j < data.sharedResourcepool[i].sharedResource.length; j++) {
              resources.push(data.sharedResourcepool[i].sharedResource[j])
            }
          }
        }
      }
      if (!resources.length) {
        if (data.sharedResource && data.sharedResource.length) {
          resources.push(data.sharedResource[0])
        }
      }
      this.resources = resources
      if (this.resources.length && (!this.event.resource_id)) {
        this.event.resource_id = this.resources[0].id
      } else if (this.resources.length && this.event.resource_id && !this.resources.map(r => r.id).includes(this.event.resource_id)) {
        this.resources.push({name:'(' + this.event.resource_name + ', #' + this.event.resource_id + ')', id:this.event.resource_id})
      }
      if (typeof this.event.showseatmap === 'undefined') {
        this.event.showseatmap = this.seatMapId ? 1 : 0
      }
    },
    async loadShowPricingModels () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/pricing.json?type=0&apikey=' + this.apiKey)
      } catch (e) {
        return
      }
      this.pricingModels = response.data
      if (this.pricingModels.length && !this.event.pricing_id) {
        this.event.pricing_id = this.defaultPricing ? this.defaultPricing : this.pricingModels[0].id
      }
    },
    async loadProductDetails (productChanged = false) {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/products/' + this.event.product_id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        return
      }
      let data = response.data
      if (data && data.channeldata && data.channeldata.duration) {
        let movieDuration = parseInt(data.channeldata.duration)
        if (movieDuration) {
          let calculatedEndDate = DateTime.fromJSDate(this.event.start_date).plus({ minutes: movieDuration })
          if (DateTime.fromJSDate(this.event.end_date).day === calculatedEndDate.day) {
            this.endDate = calculatedEndDate.toJSDate()
            this.endTime = {
              hours: calculatedEndDate.hour,
              minutes: calculatedEndDate.minute,
              seconds: 0
            }
          }
        }
      }
      if (this.defaultPricing && !this.event.pricing_id) {
        this.event.pricing_id = this.defaultPricing
        this.pricingId = this.defaultPricing
      } else if (data && data.pricing_id && productChanged) {
        this.pricingId = data.pricing_id
        this.event.pricing_id = data.pricing_id
      }
      if(!this.pricingId && this.event.pricing_id) {
        this.pricingId = this.event.pricing_id;
      }
    }
  },
  components: {
    ModelListSelect
  },
  watch: {
    productId (newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.event.product_id = newValue
        this.loadShowResources()
        this.loadProductDetails(true)
      }
    },
    seatMapId (newValue) {
      if (!newValue) {
        this.event.showseatmap = 0
      }
    }
  },
  mounted () {
    if (!this.event.product_id) {
      this.loadShowProducts()
    } else {
      this.loadShowResources()
      this.loadProductDetails()
    }
    this.loadShowPricingModels()
    this.loadShowLabels()
  }
}
</script>

<style>
.show-labels .help-block {
  margin-top: 0;
}
.show-label-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.show-label-option {
  border: 1px solid #ccc;
  border-radius: 12px;
  background: #fff;
  color: #333;
  padding: 2px 10px 2px 6px;
  font-size: 12px;
}
.show-label-option.selected {
  border-color: transparent;
}
.show-label-new {
  max-width: 360px;
}
</style>